import React, { useEffect, useState } from "react";
import { Loader2, Save, Zap, Trash2, KeyRound, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import api, { apiErr } from "@/lib/api";
import { useI18n } from "@/lib/i18n";

const PROVIDERS = [
  { id: "openai", label: "OpenAI", needsBase: true, keyHint: "sk-...", modelHint: "gpt-4o-mini" },
  { id: "gemini", label: "Google Gemini (Free Tier)", needsBase: false, keyHint: "AIza...", modelHint: "gemini-2.0-flash" },
  { id: "groq", label: "Groq (Free Tier)", needsBase: true, keyHint: "gsk_...", modelHint: "llama-3.3-70b-versatile" },
  { id: "custom", label: "Custom (OpenAI-kompatibel)", needsBase: true, keyHint: "key", modelHint: "model-name" },
];
const inputCls = "w-full rounded-lg border border-border bg-card px-3 py-2 text-sm outline-none focus:border-primary";

export default function AIProviderConfig() {
  const { lang } = useI18n();
  const de = lang === "de";
  const [c, setC] = useState(null);
  const [newKey, setNewKey] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testRes, setTestRes] = useState(null);

  const load = () => api.get("/ai/admin/config").then((r) => setC(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  if (!c) return <div className="surface p-6"><Loader2 className="animate-spin text-primary" /></div>;

  const set = (k, v) => setC((p) => ({ ...p, [k]: v }));
  const provMeta = PROVIDERS.find((p) => p.id === c.provider) || PROVIDERS[0];
  const defaults = c.provider_defaults?.[c.provider] || {};

  const onProvider = (id) => {
    const d = c.provider_defaults?.[id] || {};
    setC((p) => ({ ...p, provider: id, base_url: d.base_url || "", model: p.model || d.model || "" }));
    setTestRes(null);
  };

  const save = async () => {
    setBusy(true);
    try {
      const payload = { enabled: c.enabled, provider: c.provider, model: c.model, base_url: c.base_url, max_tokens: c.max_tokens, temperature: c.temperature };
      if (newKey.trim()) payload.api_key = newKey.trim();
      const { data } = await api.put("/ai/admin/config", payload);
      setNewKey("");
      toast.success(de ? "KI-Konfiguration gespeichert" : "AI configuration saved");
      setC((p) => ({ ...p, key_set: data.key_set, key_masked: data.key_masked }));
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const deleteKey = async () => {
    if (!window.confirm(de ? "Gespeicherten API-Schlüssel wirklich löschen?" : "Delete the stored API key?")) return;
    try { await api.delete("/ai/admin/key"); toast.success(de ? "Schlüssel gelöscht" : "Key deleted"); setC((p) => ({ ...p, key_set: false, key_masked: "" })); setTestRes(null); }
    catch (e) { toast.error(apiErr(e)); }
  };

  const test = async () => {
    setTesting(true); setTestRes(null);
    try {
      const payload = { provider: c.provider, model: c.model, base_url: c.base_url };
      if (newKey.trim()) payload.api_key = newKey.trim();
      const { data } = await api.post("/ai/admin/test", payload);
      setTestRes(data);
    } catch (e) { setTestRes({ ok: false, message: apiErr(e) }); } finally { setTesting(false); }
  };

  return (
    <div className="surface p-6 space-y-5" data-testid="ai-provider-config">
      <div className="flex items-center gap-2">
        <KeyRound size={18} className="text-primary" />
        <h3 className="font-display font-bold text-lg">{de ? "KI-Anbieter & API-Schlüssel" : "AI provider & API key"}</h3>
      </div>

      <label className="flex items-center gap-2 text-sm font-medium">
        <input type="checkbox" checked={!!c.enabled} onChange={(e) => set("enabled", e.target.checked)} className="h-4 w-4 accent-[hsl(21,100%,50%)]" data-testid="ai-enabled-toggle" />
        {de ? "KI-Assistent aktiviert" : "AI assistant enabled"}
      </label>
      <p className="text-xs text-muted-foreground -mt-2">{de ? "Ohne gültigen Anbieter/Schlüssel bleibt der Assistent deaktiviert; die Website funktioniert normal weiter." : "Without a valid provider/key the assistant stays disabled; the site keeps working normally."}</p>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-muted-foreground">{de ? "Anbieter" : "Provider"}</label>
          <select value={c.provider} onChange={(e) => onProvider(e.target.value)} className={inputCls} data-testid="ai-provider-select">
            {PROVIDERS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
          </select>
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">{de ? "Modell" : "Model"}</label>
          <input value={c.model || ""} onChange={(e) => set("model", e.target.value)} placeholder={provMeta.modelHint} className={inputCls} data-testid="ai-model-input" />
        </div>
      </div>

      {provMeta.needsBase && (
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Base URL</label>
          <input value={c.base_url || ""} onChange={(e) => set("base_url", e.target.value)} placeholder={defaults.base_url || "https://..."} className={inputCls} data-testid="ai-baseurl-input" />
        </div>
      )}

      <div>
        <label className="text-xs font-semibold text-muted-foreground">{de ? "API-Schlüssel" : "API key"}</label>
        {c.key_set && !newKey && (
          <div className="flex items-center gap-2 mb-2">
            <span className="rounded-lg bg-secondary px-3 py-1.5 text-sm font-mono" data-testid="ai-key-masked">{c.key_masked || "••••"}</span>
            <span className="text-xs text-green-500">{de ? "gespeichert (verschlüsselt)" : "stored (encrypted)"}</span>
            <button onClick={deleteKey} className="btn-ghost !py-1 !px-2 text-xs text-red-500" data-testid="ai-key-delete"><Trash2 size={13} /> {de ? "Löschen" : "Delete"}</button>
          </div>
        )}
        <input type="password" value={newKey} onChange={(e) => setNewKey(e.target.value)} autoComplete="new-password"
          placeholder={c.key_set ? (de ? "Neuen Schlüssel eingeben, um zu ersetzen…" : "Enter new key to replace…") : provMeta.keyHint}
          className={inputCls} data-testid="ai-key-input" />
        <p className="text-[11px] text-muted-foreground mt-1">{de ? "Der Schlüssel wird serverseitig verschlüsselt gespeichert und niemals vollständig zurückgegeben." : "The key is stored encrypted server-side and never returned in full."}</p>
        {c.env_fallback && !c.key_set && <p className="text-[11px] text-amber-500 mt-1">{de ? "Aktuell aktiver Fallback aus der Umgebung (Preview/ENV). Konfigurieren Sie hier einen Anbieter für die Produktion." : "An environment fallback is currently active. Configure a provider here for production."}</p>}
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        <div>
          <label className="text-xs font-semibold text-muted-foreground">{de ? "Max. Antwort-Tokens" : "Max response tokens"}</label>
          <input type="number" value={c.max_tokens} onChange={(e) => set("max_tokens", e.target.value)} className={inputCls} data-testid="ai-maxtokens-input" />
        </div>
        <div>
          <label className="text-xs font-semibold text-muted-foreground">Temperature (0–1)</label>
          <input type="number" step="0.1" min="0" max="1" value={c.temperature} onChange={(e) => set("temperature", e.target.value)} className={inputCls} data-testid="ai-temp-input" />
        </div>
      </div>

      {testRes && (
        <div className={`flex items-start gap-2 rounded-lg p-3 text-sm ${testRes.ok ? "bg-green-500/10 text-green-600" : "bg-red-500/10 text-red-600"}`} data-testid="ai-test-result">
          {testRes.ok ? <CheckCircle2 size={16} className="mt-0.5 shrink-0" /> : <XCircle size={16} className="mt-0.5 shrink-0" />}
          <div><div className="font-medium">{testRes.message}</div>{testRes.sample && <div className="text-xs opacity-80 mt-0.5">→ {testRes.sample}</div>}</div>
        </div>
      )}

      <div className="flex flex-wrap gap-2 pt-1">
        <button onClick={save} disabled={busy} className="btn-primary !py-2 !px-4 text-sm" data-testid="ai-save-btn">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Save size={15} />} {de ? "Speichern" : "Save"}
        </button>
        <button onClick={test} disabled={testing} className="btn-ghost !py-2 !px-4 text-sm" data-testid="ai-test-btn">
          {testing ? <Loader2 size={15} className="animate-spin" /> : <Zap size={15} />} {de ? "Verbindung testen" : "Test connection"}
        </button>
      </div>
    </div>
  );
}
